import { AuthCredential, User } from "firebase/auth";

import { PatientAuthType } from "@/types/enums/patient-auth-type";
import { SelfSchedulePatient } from "@/types/self-schedule";

export const AuthMethodId = {
  PASSWORD: "PASSWORD",
  GOOGLE: "GOOGLE",
  MICROSOFT: "MICROSOFT",
  PHONE: "PHONE",
  EMAIL_LINK: "EMAIL_LINK",
} as const;
export type AuthMethodId = (typeof AuthMethodId)[keyof typeof AuthMethodId];

export const AuthSurface = {
  PROVIDER_LOGIN: "PROVIDER_LOGIN",
  PATIENT_PORTAL_LOGIN: "PATIENT_PORTAL_LOGIN",
  PATIENT_SIGN_UP: "PATIENT_SIGN_UP",
  INVITE: "INVITE",
  SELF_SCHEDULE: "SELF_SCHEDULE",
  FORMS: "FORMS",
  CHAT_WIDGET: "CHAT_WIDGET",
  BILLING_PAY: "BILLING_PAY",
  RECEIPT: "RECEIPT",
  SHOP: "SHOP",
  CHECK_IN: "CHECK_IN",
} as const;
export type AuthSurface = (typeof AuthSurface)[keyof typeof AuthSurface];

export const AuthCredentialKind = AuthMethodId;
export type AuthCredentialKind = AuthMethodId;

export type AuthSuccess = {
  /** Whether this flow created or reused the clinic's patient record. */
  patientType?: PatientAuthType;
} & (
  | {
      kind: typeof AuthCredentialKind.PASSWORD;
      user: User;
      /** Patient returned by a completed phone-verified portal sign-up. */
      patient?: SelfSchedulePatient;
    }
  | {
      kind: typeof AuthCredentialKind.GOOGLE;
      user: User;
      /**
       * The popup's reusable OAuth credential, when Firebase returned one.
       * Lets a consumer continue into a flow that swaps the Firebase session
       * (phone verification) and come back to this Google identity without a
       * second popup.
       */
      googleCredential?: AuthCredential | null;
      /**
       * Patient returned by the phone-verified portal-link operation during
       * sign-up. Consumers can hand it directly to the originating patient
       * surface instead of re-reading a briefly stale claim query.
       */
      patient?: SelfSchedulePatient;
    }
  | { kind: typeof AuthCredentialKind.MICROSOFT; user: User }
  | { kind: typeof AuthCredentialKind.PHONE; patient: SelfSchedulePatient }
  | { kind: typeof AuthCredentialKind.EMAIL_LINK; user: User; email: string }
);

export type AuthAttemptStage =
  | "credential"
  | "phone_code_send"
  | "phone_code_verify"
  | "phone_claim"
  | "phone_portal_sign_in";

export type AuthAttemptOutcome =
  | "code_sent"
  | "verified_pending_claim"
  | "credential_rejected"
  | "firebase_error"
  | "unclassified_error"
  | "network_error"
  | "backend_access_denied"
  | "backend_error"
  | "signup_required"
  | "portal_handoff"
  | "cancelled"
  | "access_unconfirmed";

export interface AuthAttemptHandle {
  finish: (outcome: AuthAttemptOutcome) => void;
  fail: (error: unknown) => void;
}

export interface AuthFlowState {
  activeMethod: AuthMethodId;
  runningMethod: AuthMethodId | null;
  errors: Partial<Record<AuthMethodId, string>>;
  /**
   * Set when a method is in a sub-flow that takes over the whole screen
   * (e.g., password reset). Sibling methods hide while this is true.
   */
  isInSubFlow: boolean;
}

export interface AuthFlowActions {
  setActiveMethod: (id: AuthMethodId) => void;
  runAuth: <T>(
    method: AuthMethodId,
    fn: () => Promise<T>,
    attempt?: AuthAttemptHandle | null,
  ) => Promise<T | null>;
  startAttempt: (
    method: AuthMethodId,
    stage: AuthAttemptStage,
    isResend?: boolean,
  ) => AuthAttemptHandle | null;
  clearError: (method: AuthMethodId) => void;
  setError: (method: AuthMethodId, message: string) => void;
  setInSubFlow: (active: boolean) => void;
}

export interface AuthFlowMeta {
  surface: AuthSurface;
  enabledMethods: ReadonlySet<AuthMethodId>;
  onSuccess: (
    credential: AuthSuccess,
    attempt?: AuthAttemptHandle | null,
  ) => Promise<void> | void;
  /**
   * On surfaces where portal email/password login is NOT offered (e.g. the
   * check-in kiosk), the patient picker should not redirect portal-account
   * holders away — there is no portal path for them to use, and refusing
   * the phone claim would lock them out entirely.
   */
  allowPortalAccountHolders?: boolean;
}

export interface AuthFlowContextValue {
  state: AuthFlowState;
  actions: AuthFlowActions;
  meta: AuthFlowMeta;
}
